import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AccountCard } from '../components/profile/AccountCard';
import { ClarityScoreCard } from '../components/profile/ClarityScoreCard';
import { AchievementsCard } from '../components/profile/AchievementsCard';
import { SourceConfigCard } from '../components/profile/SourceConfigCard';
import { BookmarksCard } from '../components/profile/BookmarksCard';
import { BubbleInsightCard } from '../components/profile/BubbleInsightCard';
import { TriviaCard } from '../components/profile/TriviaCard';
import { ComparisonCard } from '../components/profile/ComparisonCard';
import { DAILY_TRIVIA } from '../data/dailyTrivia';
import { logError } from '../lib/logger';
import { redirectToGoogleSignIn } from '../lib/googleAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getClarityLevel = (score) => {
  if (score >= 80) return { level: 'Crystal Clear', color: 'text-green-600', desc: 'Exceptional media literacy' };
  if (score >= 60) return { level: 'Clear Vision', color: 'text-blue-600', desc: 'Strong perspective diversity' };
  if (score >= 40) return { level: 'Getting Clearer', color: 'text-yellow-600', desc: 'Improving awareness' };
  return { level: 'In the Bubble', color: 'text-gray-600', desc: 'Limited source diversity' };
};

const ProfilePage = () => {
  const location = useLocation();
  const [authUser, setAuthUser] = useState(location.state?.user || null);
  const [localUser, setLocalUser] = useState(null);
  const [sources, setSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState(location.state?.user?.selected_sources || []);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [bookmarks, setBookmarks] = useState([]);
  const [todayTrivia] = useState(DAILY_TRIVIA[Math.floor(Math.random() * DAILY_TRIVIA.length)]);

  const savePreferences = useCallback(async (srcIds, allSourcesCount) => {
    const score = allSourcesCount > 0 ? Math.round(Math.min(100, (srcIds.length / allSourcesCount) * 100)) : 0;
    const res = await fetch(`${BACKEND_URL}/api/auth/preferences`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_sources: srcIds, clarity_score: score }),
    });
    if (res.ok) {
      const updated = await res.json();
      setAuthUser(updated);
      setSelectedSources(updated.selected_sources || []);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const init = async () => {
      let sourcesList = [];
      try {
        const res = await fetch(`${BACKEND_URL}/api/sources`);
        sourcesList = await res.json();
        setSources(sourcesList);
      } catch (e) {
        logError('ProfilePage:fetchSources', e);
      }

      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const u = await res.json();
          setAuthUser(u);
          setSelectedSources(u.selected_sources || []);
          fetchBookmarks();
          // Flush any sources selected before Google sign-in
          const pending = localStorage.getItem('pending_sources');
          if (pending) {
            localStorage.removeItem('pending_sources');
            const pendingIds = JSON.parse(pending);
            if (pendingIds.length > 0) {
              await savePreferences(pendingIds, sourcesList.length);
            }
          }
          return;
        }
      } catch (e) {
        logError('ProfilePage:authCheck', e); // not authenticated yet, fall back to guest/local
      }

      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        setLocalUser(parsed);
        setSelectedSources(parsed.sources || []);
      }
    };
    init();
  }, [savePreferences]);

  const toggleSource = (sourceId) => {
    setSelectedSources((prev) =>
      prev.includes(sourceId) ? prev.filter((id) => id !== sourceId) : [...prev, sourceId]
    );
  };

  const fetchBookmarks = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/bookmarks`, { credentials: 'include' });
      if (res.ok) setBookmarks(await res.json());
    } catch (e) {
      logError('ProfilePage:fetchBookmarks', e);
    }
  };

  const handleGoogleSignIn = () => {
    localStorage.setItem('pending_sources', JSON.stringify(selectedSources));
    redirectToGoogleSignIn('/profile');
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    const ok = await savePreferences(selectedSources, sources.length);
    setSaving(false);
    setSaveMessage(ok ? 'Preferences saved to your profile!' : 'Failed to save. Please try again.');
    if (ok) setShowConfig(false);
    setTimeout(() => setSaveMessage(''), 4000);
  };

  const handleSaveLocal = () => {
    const userData = { sources: selectedSources, clarityScore: calculateClarityScore() };
    localStorage.setItem('user', JSON.stringify(userData));
    setLocalUser(userData);
    setShowConfig(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      logError('ProfilePage:logout', e);
    }
    setAuthUser(null);
    setSelectedSources([]);
  };

  const calculateClarityScore = () => {
    if (sources.length === 0) return 0;
    return Math.round(Math.min(100, (selectedSources.length / sources.length) * 100));
  };

  const currentScore = authUser?.clarity_score || calculateClarityScore();
  const clarityLevel = getClarityLevel(currentScore);
  const isLoggedIn = !!authUser;
  const bubbleBreaks = authUser?.bubble_breaks || localUser?.bubbleBreaks || 0;

  return (
    <div className="min-h-[calc(100vh-112px)] sm:min-h-[calc(100vh-128px)] px-4 py-6 sm:py-8 lg:py-12" data-testid="profile-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-3 sm:mb-4">
            Your Clarity Profile
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600">
            Track your journey to unbiased news consumption
          </p>
        </div>

        <AccountCard
          authUser={authUser}
          isLoggedIn={isLoggedIn}
          onLogout={handleLogout}
          onGoogleSignIn={handleGoogleSignIn}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8">
          <ClarityScoreCard
            currentScore={currentScore}
            clarityLevel={clarityLevel}
            selectedCount={selectedSources.length}
            totalSources={sources.length}
            bubbleBreaks={bubbleBreaks}
          />
          <AchievementsCard selectedCount={selectedSources.length} bubbleBreaks={bubbleBreaks} />
        </div>

        <BubbleInsightCard bubbleBreakSources={authUser?.bubble_break_sources} sources={sources} />

        <SourceConfigCard
          showConfig={showConfig}
          onToggleConfig={() => setShowConfig(!showConfig)}
          saveMessage={saveMessage}
          selectedSources={selectedSources}
          sources={sources}
          onToggleSource={toggleSource}
          isLoggedIn={isLoggedIn}
          saving={saving}
          onSavePreferences={handleSavePreferences}
          onGoogleSignIn={handleGoogleSignIn}
          onSaveLocal={handleSaveLocal}
        />

        <TriviaCard trivia={todayTrivia} />

        <BookmarksCard bookmarks={bookmarks} />

        {(isLoggedIn || localUser) && <ComparisonCard currentScore={currentScore} />}
      </div>
    </div>
  );
};

export default ProfilePage;
